/**
 * AtlasSmartCompareFiltered — Advisor smart-compare filtered-list view.
 *
 * Batch 2 revision (2026-08-07): Reverted from the accidental Explore
 * card reuse. Per PRD Cardinal Rules (see /app/memory/PRD.md):
 *   - Atlas UI ≠ Explore UI.
 *   - No shared card / modal components between advisor and traveler.
 * Cards here are Atlas-native: information-dense, thumbnail (not hero
 * photo), advisor actions (`Save to client`, `Add to itinerary`,
 * `Book`) — NO swipe / Heart / Skip / Undo / Dream it.
 *
 * Pipeline per column (fires in parallel across all columns):
 *   1. GET  /api/atlas/topics?q=<entity>&limit=1        → topic slug
 *   2. POST /api/accommodations/search                   → Stay22 offers
 *      (page_size=30, guests+dates from parsed plan)
 *   3. POST /api/atlas/smart-compare/enrich             → Exa scoring +
 *      enrichment tag extraction (best-match ordering + tag chips)
 *
 * Responsive column count:
 *   1     → single wide column (max-w-2xl)
 *   2     → grid-cols-2
 *   3–4   → horizontal scroll, each column ~360px
 *   5+    → 2-row grid-cols-3
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Loader2, RefreshCcw, Sparkles, Search as SearchIcon,
  ExternalLink, Bookmark, CalendarPlus, Star, X as XIcon, Info,
  ArrowDownWideNarrow, ArrowUpNarrowWide, Award,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import EarnPointsCta from "@/components/EarnPointsCta";

const _PAGE_INITIAL = 10;
const _PAGE_STEP    = 10;
const _FETCH_SIZE   = 30;

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function priceStr(offer) {
  const s = offer.supplier_offers?.[0];
  if (typeof s?.price !== "number") return null;
  return `${s.currency || "$"} ${Math.round(s.price)}`;
}

/** Atlas-native Book Now — delayed-open with click log. Same server
 *  contract as the traveler flow, DIFFERENT visual affordance
 *  (compact secondary-style button, not the gold traveler pill). */
async function bookOffer(offer) {
  const s = offer.supplier_offers?.[0];
  if (!s?.deeplink_url) {
    toast.error("Booking link unavailable.");
    return;
  }
  let tab = null;
  try { tab = window.open("about:blank", "_blank"); } catch {}
  try {
    const { data } = await api.post("/accommodations/click", {
      property_id:   offer.property_id,
      property_name: offer.name,
      supplier:      s.supplier,
      supplier_code: s.supplier_code,
      price:         s.price,
      currency:      s.currency,
      deeplink_used: s.deeplink_url,
      context:       "atlas",
    });
    const url = data?.url || s.deeplink_url;
    if (tab && !tab.closed) { try { tab.location.href = url; } catch {} }
    else if (url) window.open(url, "_blank");
  } catch {
    if (tab && !tab.closed) { try { tab.location.href = s.deeplink_url; } catch {} }
  }
}

function saveSnapshotFromOffer(offer, entity, query) {
  // Matches the shape AtlasSearchPage's openSavePicker builds so the
  // ClientPickerModal can consume smart-compare cards identically.
  const s = offer.supplier_offers?.[0] || {};
  return {
    title: offer.name || "",
    entity_name: offer.name || "",
    entity_slug: null,
    entity_slugs: [],
    url: s.deeplink_url || "",
    image_url: offer.image || null,
    snippet: offer.subtitle || "",
    source: "smart_compare_card",
    publication_name: null,
    article_url: null,
    article_title: null,
    match_percentage: typeof offer._score === "number"
      ? Math.round(offer._score * 100) : null,
    saved_from_query: (query || "").trim(),
    smart_compare_entity: entity,
  };
}


async function loadStay22(entity, plan) {
  const trav = plan.travelers || { adults: 2, children_count: 0 };
  // 2026-08-09 — forward filter chip picks so the hotel-hub fallback
  // + property_types hard-filter fire on the Explore path too. Maps
  // filter.accommodation_type ("resort"/"hotel"/"all_inclusive") and
  // filter.tags (includes "all_inclusive") into `property_types`.
  const chipTypes = [];
  const acc = (plan.filter?.accommodation_type || "").toLowerCase();
  if (acc) chipTypes.push(acc);
  for (const t of (plan.filter?.tags || [])) {
    const norm = String(t || "").toLowerCase().replace(/-/g, "_");
    if (norm === "all_inclusive" && !chipTypes.includes("all_inclusive"))
      chipTypes.push("all_inclusive");
  }
  const payload = {
    location: entity,
    guests: {
      adults:   Math.max(1, Math.min(20, trav.adults || 2)),
      children: Array(Math.max(0, Math.min(12, trav.children_count || 0))).fill(0),
    },
    check_in:  todayPlus(30),
    check_out: todayPlus(33),
    page_size: _FETCH_SIZE,
  };
  if (chipTypes.length > 0) payload.filters = { property_types: chipTypes };
  const topicP = api.get(`/atlas/topics?q=${encodeURIComponent(entity)}&limit=1`)
    .then(({ data }) => (data?.topics || [])[0] || null)
    .catch(() => null);
  const stayP = api.post("/accommodations/search", payload)
    .then(({ data }) => data)
    .catch((err) => ({ __error: err?.response?.data?.detail || "search failed" }));

  const [topicHit, stay] = await Promise.all([topicP, stayP]);
  return {
    topic: topicHit,
    offers: stay?.offers || [],
    cache_hit: !!stay?.cache_hit,
    rate_limited: !!stay?.rate_limited,
    error: stay?.__error || null,
  };
}

/** Stage 2 — Exa enrichment. Fires AFTER Stay22 returns because we
 *  need the offer list. Returns null on failure so the column keeps
 *  rendering with backend-order offers. */
async function loadEnrichment(entity, plan, offers) {
  if (!offers || offers.length === 0) return null;
  const enrichBody = {
    entity, plan,
    offers: offers.map((o) => ({
      property_id: o.property_id,
      name: o.name || "",
      subtitle: o.subtitle || null,
      property_type: o.property_type || null,
      rating: typeof o.rating === "number" ? o.rating : null,
      price: typeof o.price === "number" ? o.price : null,
      supplier_offers: o.supplier_offers || [],
    })),
  };
  try {
    const { data } = await api.post("/atlas/smart-compare/enrich", enrichBody);
    return data;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[atlas.smart-compare.enrich] failed", err);
    return null;
  }
}


function BestMatchRibbon({ subScores }) {
  const [open, setOpen] = useState(false);
  const rows = [
    ["Name match",     subScores?.name_overlap,    0.40],
    ["Type match",     subScores?.type_match,      0.20],
    ["Amenity match",  subScores?.amenity_overlap, 0.20],
    ["Rating",         subScores?.rating,          0.10],
    ["Price percentile", subScores?.price_pctile,  0.10],
  ];
  return (
    <div className="relative"
         onMouseEnter={() => setOpen(true)}
         onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="atlas-smart-best-ribbon"
        className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full bg-tb-gold-bright text-tb-navy font-hand text-[9px] font-semibold uppercase tracking-wider shadow-sm cursor-help"
      >
        <Sparkles className="w-2.5 h-2.5" /> Best
        <Info className="w-2.5 h-2.5 opacity-70" />
      </button>
      {open && subScores && (
        <div
          data-testid="atlas-smart-best-popover"
          className="absolute z-30 top-6 left-0 w-56 rounded-lg border border-tb-navy/20 bg-white shadow-lg p-3"
          role="tooltip"
        >
          <div className="text-[10px] uppercase tracking-widest text-tb-navy/50 font-hand mb-1.5">
            Why ranked first
          </div>
          <div className="space-y-1">
            {rows.map(([label, v, w]) => (
              <div key={label} className="flex items-center justify-between text-[11px] text-tb-navy font-hand">
                <span>{label}</span>
                <span className="tabular-nums">
                  {v != null ? `${Math.round(v * 100)}%` : "—"}
                  <span className="text-tb-navy/40 ml-1">·{(w * 100).toFixed(0)}w</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AtlasResortCard({ offer, isBest, entity, query, onOpenSavePicker,
                          onOpen, sessionClient }) {
  const tags = offer._enrichment_tags || [];
  const price = priceStr(offer);
  const supplier = offer.supplier_offers?.[0]?.supplier;
  const scorePct = typeof offer._score === "number" ? Math.round(offer._score * 100) : null;

  const onSave = () => {
    const snap = saveSnapshotFromOffer(offer, entity, query);
    if (sessionClient) {
      snap._preselect_client_id = sessionClient.client_id;
    }
    if (typeof onOpenSavePicker === "function") {
      onOpenSavePicker(snap);
    } else {
      toast.info("Save to client — open a client picker to attach.");
    }
  };
  const onItinerary = () => {
    toast.info("Add to itinerary — coming soon.");
  };

  return (
    <article
      data-testid={`atlas-smart-card-${offer.property_id}`}
      className={
        "relative flex gap-3 rounded-xl border bg-white p-3 hover:border-tb-navy/30 transition " +
        (isBest ? "border-tb-gold-bright/70 ring-1 ring-tb-gold-bright/30" : "border-tb-navy/15")
      }
    >
      {/* Thumbnail — small, not a hero image. Clickable → open detail modal (D4). */}
      <button
        type="button"
        onClick={onOpen}
        aria-label="Open details"
        className="w-24 h-24 md:w-28 md:h-28 flex-shrink-0 rounded-lg overflow-hidden bg-tb-navy/5 relative hover:opacity-90 tb-tap"
        data-testid={`atlas-smart-card-open-${offer.property_id}`}
      >
        {offer.image ? (
          <img src={offer.image} alt="" loading="lazy"
               className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-tb-navy/80 to-tb-navy/95 flex items-center justify-center">
            <span className="text-tb-gold-bright/80 text-[10px] font-hand uppercase tracking-widest">
              no photo
            </span>
          </div>
        )}
        {isBest && (
          <div className="absolute -top-1 -left-1"
               data-testid={`atlas-smart-card-best-${offer.property_id}`}>
            <BestMatchRibbon subScores={offer._sub_scores} />
          </div>
        )}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="min-w-0">
          <h4
            className="text-tb-navy font-semibold text-sm leading-tight line-clamp-2"
            style={{ fontFamily: '"Playfair Display", serif' }}
            data-testid={`atlas-smart-card-name-${offer.property_id}`}
          >
            {offer.name}
          </h4>
          {offer.subtitle && (
            <p className="mt-0.5 text-tb-navy/55 font-hand text-[11px] line-clamp-1">
              {offer.subtitle}
            </p>
          )}
          <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] font-hand">
            {price && (
              <span className="text-tb-navy font-semibold"
                    data-testid={`atlas-smart-card-price-${offer.property_id}`}>
                {price}
              </span>
            )}
            {supplier && (
              <span className="text-tb-navy/50">· {supplier}</span>
            )}
            {typeof offer.rating === "number" && (
              <span className="inline-flex items-center gap-0.5 text-tb-navy/60">
                <Star className="w-2.5 h-2.5" /> {offer.rating.toFixed(1)}
              </span>
            )}
            {scorePct !== null && (
              <span className="text-tb-navy/40 ml-auto text-[10px]"
                    title="Smart-compare match score">
                {scorePct}% match
              </span>
            )}
          </div>
        </div>

        {/* Enrichment tag row — Atlas-style bordered chips */}
        {tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1"
               data-testid={`atlas-smart-card-tags-${offer.property_id}`}>
            {tags.slice(0, 4).map((t) => (
              <span key={t}
                className="inline-flex items-center h-4 px-1.5 rounded-full border border-tb-navy/15 text-tb-navy/70 font-hand text-[9px] uppercase tracking-wider">
                {t.replace(/-/g, " ")}
              </span>
            ))}
          </div>
        )}

        {/* Actions — Atlas-appropriate button styling. NO swipe/heart/skip/undo/dream. */}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={onSave}
            data-testid={`atlas-smart-card-save-${offer.property_id}`}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md border border-tb-navy/25 bg-white text-tb-navy font-hand text-[11px] hover:bg-tb-navy/5"
            title="Save to client portfolio"
          >
            <Bookmark className="w-3 h-3" /> Save
          </button>
          <button
            type="button"
            onClick={onItinerary}
            data-testid={`atlas-smart-card-itinerary-${offer.property_id}`}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md border border-tb-navy/25 bg-white text-tb-navy font-hand text-[11px] hover:bg-tb-navy/5"
            title="Add to itinerary (coming soon)"
          >
            <CalendarPlus className="w-3 h-3" /> Itinerary
          </button>
          <button
            type="button"
            onClick={() => bookOffer(offer)}
            data-testid={`atlas-smart-card-book-${offer.property_id}`}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-tb-navy text-tb-gold-bright font-hand text-[11px] hover:brightness-110"
            title="Open supplier booking page"
          >
            <ExternalLink className="w-3 h-3" /> Book
          </button>
        </div>
        <EarnPointsCta tone="atlas" multiplier={offer?.booking_override?.rewards_multiplier ?? 1} />
      </div>
    </article>
  );
}


function SmartCompareColumn({ entity, plan, query, colWidthClass, onOpenSavePicker, sessionClient }) {
  const [state, setState] = useState({ loading: true, enriching: false });
  const [shown, setShown] = useState(_PAGE_INITIAL);
  // D1 — client-side sort. Default best (server-sorted); toggle
  // rebuilds visible order without re-fetching.
  const [sort, setSort] = useState("best");
  // D4 — detail modal for the tapped card.
  const [detailFor, setDetailFor] = useState(null);

  const reload = useCallback(async () => {
    setState({ loading: true, enriching: false });
    setShown(_PAGE_INITIAL);

    const stage1 = await loadStay22(entity, plan);
    // Paint immediately — user sees cards as soon as Stay22 replies.
    setState({
      loading: false,
      topic:  stage1.topic,
      offers: stage1.offers,
      cache_hit: stage1.cache_hit,
      rate_limited: stage1.rate_limited,
      error: stage1.error,
      // Only fire Stage 2 if we actually have offers to score.
      enriching: !stage1.error && !stage1.rate_limited && stage1.offers.length > 0,
    });

    // Stage 2 — kick off Exa in the background, merge when it lands.
    if (!stage1.error && !stage1.rate_limited && stage1.offers.length > 0) {
      const scored = await loadEnrichment(entity, plan, stage1.offers);
      setState((prev) => {
        // If reload() ran again while we were awaiting, don't clobber.
        if (prev.loading) return prev;
        if (!scored || !Array.isArray(scored.scored)) {
          return { ...prev, enriching: false };
        }
        const byId = new Map();
        for (const s of scored.scored) byId.set(s.property_id, s);
        const merged = (prev.offers || []).map((o) => {
          const s = byId.get(o.property_id);
          return { ...o, _score: s?.score ?? 0, _enrichment_tags: s?.enrichment_tags || [], _sub_scores: s?.sub_scores || null };
        });
        merged.sort((a, b) => (b._score || 0) - (a._score || 0));
        return { ...prev, offers: merged, enriching: false };
      });
    }
  }, [entity, plan]);

  useEffect(() => { reload(); }, [reload]);

  const offers = state.offers || [];
  const sortedOffers = useMemo(() => {
    if (sort === "price") {
      // Price low → high. Missing price → last.
      return [...offers].sort((a, b) => {
        const pa = a.supplier_offers?.[0]?.price;
        const pb = b.supplier_offers?.[0]?.price;
        if (pa == null && pb == null) return 0;
        if (pa == null) return 1;
        if (pb == null) return -1;
        return pa - pb;
      });
    }
    if (sort === "rating") {
      return [...offers].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }
    return offers;   // best (server-sorted)
  }, [offers, sort]);
  const visible = useMemo(() => sortedOffers.slice(0, shown), [sortedOffers, shown]);
  const hasMore = shown < sortedOffers.length;

  return (
    <div className={`${colWidthClass} flex-shrink-0`}
         data-testid={`atlas-smart-column-${entity.toLowerCase().replace(/\s+/g, "-")}`}>
      <ColumnOverview entity={entity} topic={state.topic} loading={state.loading} />

      {/* D1 — sort toggle. Only visible once we have offers. */}
      {!state.loading && (state.offers || []).length > 0 && (
        <div className="mt-2 flex items-center gap-1"
             data-testid={`atlas-smart-column-sort-${entity}`}>
          {[
            ["best",   "Best match", Award],
            ["price",  "Price ↑",    ArrowUpNarrowWide],
            ["rating", "Rating ↓",   ArrowDownWideNarrow],
          ].map(([k, label, Icon]) => (
            <button key={k} type="button"
              onClick={() => setSort(k)}
              data-testid={`atlas-smart-column-sort-${entity}-${k}`}
              className={
                "inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-hand uppercase tracking-wider " +
                (sort === k
                  ? "bg-tb-navy text-tb-gold-bright"
                  : "border border-tb-navy/15 text-tb-navy/60 hover:border-tb-navy/40")
              }
            >
              <Icon className="w-2.5 h-2.5" /> {label}
            </button>
          ))}
        </div>
      )}

      {state.loading && (
        <div className="mt-3 space-y-2" data-testid={`atlas-smart-column-loading-${entity}`}>
          {[1,2,3].map((i) => (
            <div key={i} className="rounded-xl border border-tb-navy/10 h-24 bg-tb-navy/[0.03] animate-pulse" />
          ))}
        </div>
      )}

      {!state.loading && state.rate_limited && (
        <div className="mt-3 rounded-xl border-2 border-amber-300 bg-amber-50/60 p-3"
             data-testid={`atlas-smart-column-rate-limited-${entity}`}>
          <p className="text-sm text-amber-900 font-hand">
            Live rates temporarily unavailable for {entity}.
          </p>
          <button type="button" onClick={reload}
            className="mt-2 h-8 px-3 rounded-md border border-amber-600 text-amber-900 font-hand text-xs inline-flex items-center gap-1.5">
            <RefreshCcw className="w-3.5 h-3.5" /> Retry
          </button>
        </div>
      )}

      {!state.loading && state.error && !state.rate_limited && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3"
             data-testid={`atlas-smart-column-error-${entity}`}>
          <p className="text-sm text-red-700 font-hand">{state.error}</p>
        </div>
      )}

      {!state.loading && !state.error && !state.rate_limited && offers.length === 0 && (
        <div className="mt-3 rounded-xl border border-dashed border-tb-navy/20 bg-tb-navy/[0.02] p-4"
             data-testid={`atlas-smart-column-empty-${entity}`}>
          <p className="font-hand text-tb-navy/60 text-sm">
            No properties matched for {entity} on default dates.
          </p>
        </div>
      )}

      {!state.loading && visible.length > 0 && (
        <>
          {state.enriching && (
            <div
              data-testid={`atlas-smart-column-ranking-${entity}`}
              className="mt-3 mb-1 inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-tb-navy/[0.06] border border-tb-navy/15 text-tb-navy/60 font-hand text-[10px] uppercase tracking-wider"
            >
              <Loader2 className="w-3 h-3 animate-spin" /> Ranking…
            </div>
          )}
          <div className="mt-3 space-y-2"
               data-testid={`atlas-smart-column-cards-${entity}`}>
            {visible.map((o, idx) => (
              <AtlasResortCard
                key={o.property_id}
                offer={o}
                isBest={sort === "best" && !state.enriching && idx === 0 && typeof o._score === "number" && o._score > 0}
                entity={entity}
                query={query}
                onOpenSavePicker={onOpenSavePicker}
                onOpen={() => setDetailFor(o)}
                sessionClient={sessionClient}
              />
            ))}
          </div>
          {hasMore && (
            <button type="button"
              onClick={() => setShown((n) => Math.min(sortedOffers.length, n + _PAGE_STEP))}
              data-testid={`atlas-smart-column-loadmore-${entity}`}
              className="mt-3 w-full h-9 rounded-md border border-tb-navy/20 bg-white font-hand text-xs text-tb-navy hover:bg-tb-navy/5 inline-flex items-center justify-center gap-1.5"
            >
              Load more ({Math.min(_PAGE_STEP, sortedOffers.length - shown)})
            </button>
          )}
        </>
      )}

      {/* D4 — detail modal, Atlas-native (NOT the Explore DetailModal). */}
      {detailFor && (
        <AtlasSmartDetailModal
          offer={detailFor}
          entity={entity}
          query={query}
          onClose={() => setDetailFor(null)}
          onOpenSavePicker={onOpenSavePicker}
          sessionClient={sessionClient}
        />
      )}
    </div>
  );
}


/** D4 — Atlas-native detail modal for a smart-compare card. Shows
 *  photo, name, subtitle, all supplier prices, enrichment tags, and
 *  the best-match score breakdown. Deliberately NOT the Explore
 *  DetailModal — cardinal rule 3. */
function AtlasSmartDetailModal({ offer, entity, query, onClose, onOpenSavePicker, sessionClient }) {
  const suppliers = offer.supplier_offers || [];
  const priced = suppliers.filter((s) => typeof s.price === "number");
  const sub = offer._sub_scores;
  const scorePct = typeof offer._score === "number" ? Math.round(offer._score * 100) : null;
  const rows = sub ? [
    ["Name match",       sub.name_overlap,    0.40],
    ["Type match",       sub.type_match,      0.20],
    ["Amenity match",    sub.amenity_overlap, 0.20],
    ["Rating",           sub.rating,          0.10],
    ["Price percentile", sub.price_pctile,    0.10],
  ] : [];
  const onSave = () => {
    const snap = saveSnapshotFromOffer(offer, entity, query);
    if (sessionClient) snap._preselect_client_id = sessionClient.client_id;
    if (typeof onOpenSavePicker === "function") onOpenSavePicker(snap);
  };
  return (
    <div
      data-testid="atlas-smart-detail-modal"
      className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
      >
        <button type="button" onClick={onClose}
          data-testid="atlas-smart-detail-close"
          className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-white/95 border border-tb-navy/20 flex items-center justify-center hover:border-tb-navy/50"
          aria-label="Close">
          <XIcon className="w-4 h-4 text-tb-navy" />
        </button>
        {offer.image && (
          <img src={offer.image} alt="" className="w-full h-56 object-cover" />
        )}
        <div className="p-4">
          <h3 className="text-tb-navy font-semibold text-lg leading-tight"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            {offer.name}
          </h3>
          {offer.subtitle && (
            <p className="mt-0.5 text-tb-navy/60 font-hand text-xs">{offer.subtitle}</p>
          )}
          {scorePct !== null && (
            <div className="mt-2 text-[10px] uppercase tracking-widest font-hand text-tb-navy/60 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-tb-gold-bright" />
              {scorePct}% smart-compare match
            </div>
          )}

          {/* Enrichment tags */}
          {offer._enrichment_tags?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1"
                 data-testid="atlas-smart-detail-tags">
              {offer._enrichment_tags.map((t) => (
                <span key={t}
                  className="inline-flex items-center h-5 px-2 rounded-full border border-tb-navy/20 text-tb-navy/70 font-hand text-[10px] uppercase tracking-wider">
                  {t.replace(/-/g, " ")}
                </span>
              ))}
            </div>
          )}

          {/* Score breakdown */}
          {sub && (
            <div className="mt-4 rounded-lg border border-tb-navy/15 p-3"
                 data-testid="atlas-smart-detail-scorecard">
              <div className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/50 mb-1.5">
                Score breakdown
              </div>
              {rows.map(([label, v, w]) => (
                <div key={label} className="flex items-center justify-between text-[11px] text-tb-navy font-hand py-0.5">
                  <span>{label}</span>
                  <span className="tabular-nums">
                    {v != null ? `${Math.round(v * 100)}%` : "—"}
                    <span className="text-tb-navy/40 ml-1">·{(w * 100).toFixed(0)}w</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Supplier prices */}
          {priced.length > 0 && (
            <div className="mt-4 rounded-lg border border-tb-navy/15 p-3">
              <div className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/50 mb-1.5">
                Suppliers
              </div>
              {priced.map((s) => (
                <div key={s.supplier_code || s.supplier}
                     className="flex items-center justify-between py-1 text-sm">
                  <span className="text-tb-navy font-hand">{s.supplier}</span>
                  <span className="text-tb-navy/70 font-mono text-xs">
                    {`${s.currency || "$"} ${Math.round(s.price)}`}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Actions — same Atlas trio */}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <button type="button" onClick={onSave}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-tb-navy/25 bg-white text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
              <Bookmark className="w-3 h-3" /> Save {sessionClient ? `to ${sessionClient.client_name || "client"}` : ""}
            </button>
            <button type="button" onClick={() => toast.info("Add to itinerary — coming soon.")}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-tb-navy/25 bg-white text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
              <CalendarPlus className="w-3 h-3" /> Itinerary
            </button>
            <button type="button" onClick={() => bookOffer(offer)}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-tb-navy text-tb-gold-bright font-hand text-xs hover:brightness-110">
              <ExternalLink className="w-3 h-3" /> Book
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function ColumnOverview({ entity, topic, loading }) {
  return (
    <div className="rounded-xl border border-tb-navy/15 bg-white p-3"
         data-testid={`atlas-smart-column-overview-${entity}`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h3
          className="text-tb-navy text-lg font-semibold leading-tight"
          style={{ fontFamily: '"Playfair Display", serif' }}
          data-testid={`atlas-smart-column-title-${entity}`}
        >
          {topic?.name || entity}
        </h3>
        {topic?.category && (
          <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/50">
            {topic.category.replace(/_/g, " ")}
          </span>
        )}
      </div>
      {topic?.fact_count > 0 ? (
        <p className="mt-1 text-tb-navy/60 font-hand text-xs">
          {topic.fact_count} fact{topic.fact_count === 1 ? "" : "s"} in Atlas
          {topic.slug ? (
            <a href={`/atlas/topics/${topic.slug}`}
               className="ml-1.5 underline underline-offset-2 hover:text-tb-navy">
              open folder →
            </a>
          ) : null}
        </p>
      ) : loading ? (
        <p className="mt-1 text-tb-navy/40 font-hand text-xs italic">Loading Atlas overview…</p>
      ) : (
        <p className="mt-1 text-tb-navy/40 font-hand text-xs italic">
          Not yet in Atlas — showing live Stay22 inventory only.
        </p>
      )}
    </div>
  );
}


export default function AtlasSmartCompareFiltered({ plan, query, onOpenSavePicker,
                                                     sessionClient, onClearSessionClient }) {
  const entities = plan?.entities || [];
  const n = entities.length;

  let outerCls = "";
  let colWidthCls = "";
  if (n <= 1) {
    outerCls    = "max-w-2xl mx-auto";
    colWidthCls = "w-full";
  } else if (n === 2) {
    outerCls    = "grid grid-cols-1 md:grid-cols-2 gap-4";
    colWidthCls = "w-full";
  } else if (n <= 4) {
    outerCls    = "flex gap-4 overflow-x-auto pb-4";
    colWidthCls = "w-[360px]";
  } else {
    outerCls    = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4";
    colWidthCls = "w-full";
  }

  const chipRow = (() => {
    const f = plan?.filter || {};
    const t = plan?.travelers || {};
    const chips = [];
    if (f.accommodation_type) chips.push(f.accommodation_type);
    (f.tags || []).slice(0, 4).forEach((x) => chips.push(x.replace(/_/g, " ")));
    (f.style_hints || []).slice(0, 2).forEach((x) => chips.push(x.replace(/_/g, " ")));
    if (t.children_count) {
      const ages = t.children_age_hints;
      const ageStr = ages?.length ? ` (${ages.join(", ")})` : "";
      chips.push(`${t.adults}A · ${t.children_count} kid${t.children_count === 1 ? "" : "s"}${ageStr}`);
    } else if (t.adults) {
      chips.push(`${t.adults}A`);
    }
    if (chips.length === 0) return null;
    return (
      <div className="mb-4 flex flex-wrap items-center gap-1.5"
           data-testid="atlas-smart-plan-chips">
        <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/50">
          Smart compare
        </span>
        {chips.map((c, i) => (
          <span key={`${c}-${i}`}
            className="inline-flex items-center h-6 px-2.5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-[10px] uppercase tracking-wider">
            {c}
          </span>
        ))}
        <span className="text-[10px] font-hand text-tb-navy/40 ml-1">
          · confidence {(plan.confidence * 100).toFixed(0)}%
        </span>
      </div>
    );
  })();

  if (n === 0) {
    return (
      <div className="mt-6 rounded-2xl border border-dashed border-tb-navy/25 bg-tb-navy/[0.02] p-8 text-center"
           data-testid="atlas-smart-empty-entities">
        <SearchIcon className="w-6 h-6 text-tb-navy/40 mx-auto" />
        <p className="mt-2 font-hand text-tb-navy/60 text-sm">
          Couldn't identify anything to compare from your query. Try naming
          the destinations, e.g. <span className="italic">"Jamaica vs St Thomas"</span>.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6" data-testid="atlas-smart-compare-filtered">
      {chipRow}
      {sessionClient && (
        <div className="mb-3 inline-flex items-center gap-2 h-8 px-3 rounded-full bg-tb-navy/[0.05] border border-tb-navy/20"
             data-testid="atlas-smart-session-client-chip">
          <Bookmark className="w-3.5 h-3.5 text-tb-navy/70" />
          <span className="text-[11px] font-hand text-tb-navy">
            Saving to <b>{sessionClient.client_name || "client"}</b>
          </span>
          <button type="button"
            onClick={onClearSessionClient}
            data-testid="atlas-smart-session-client-clear"
            className="text-tb-navy/50 hover:text-red-500 tb-tap"
            aria-label="Clear session client"
          >
            <XIcon className="w-3 h-3" />
          </button>
        </div>
      )}
      <div className={outerCls} data-testid="atlas-smart-columns">
        {entities.map((entity) => (
          <SmartCompareColumn key={entity}
            entity={entity}
            plan={plan}
            query={query}
            colWidthClass={colWidthCls}
            onOpenSavePicker={onOpenSavePicker}
            sessionClient={sessionClient}
          />
        ))}
      </div>
    </div>
  );
}
